import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getGrns } from '../api/grn';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function getGrnId(grn) {
  return grn?.grn_id || grn?.id || '';
}

function defaultGrnLabel(grn) {
  return grn?.grn_number || getGrnId(grn) || '';
}

function isPostedGrn(grn) {
  return (grn.posting_status || grn.status) === 'POSTED_TO_STOCK';
}

/** Debounced posted-GRN search — min 2 chars, 300ms (same as InvItemAutocomplete). */
export default function InvGrnAutocomplete({
  value,
  onChange,
  getOptionLabel = defaultGrnLabel,
  label = 'Posted GRN',
  placeholder = 'Search GRN number',
  postedOnly = true,
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedGrn(null);
      return;
    }
    if (selectedGrn && getGrnId(selectedGrn) === value) return;
    const found = options.find((grn) => getGrnId(grn) === value) || null;
    if (found) setSelectedGrn(found);
  }, [value, options, selectedGrn]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!open) return;

    const inputQuery = inputValue.trim();
    const query = debouncedSearch;
    const shouldSearch = query.length >= MIN_SEARCH_LENGTH;
    const pendingSearch = inputQuery.length >= MIN_SEARCH_LENGTH && inputQuery !== query;

    if (pendingSearch) {
      setOptions([]);
      return;
    }

    if (!shouldSearch) {
      setOptions([]);
      return;
    }

    const requestId = ++requestIdRef.current;

    async function fetchGrns() {
      setOptions([]);
      setLoading(true);
      try {
        const params = { grn_number: query, status: 'POSTED_TO_STOCK' };
        const data = await getGrns(params);
        if (requestId !== requestIdRef.current) return;

        let grns = normalizeList(data);
        if (postedOnly) {
          grns = grns.filter(isPostedGrn);
        }
        setOptions(grns);
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchGrns();
  }, [debouncedSearch, open, inputValue, postedOnly]);

  const displayOptions = useMemo(() => {
    if (isSearching || !selectedGrn) return options;
    const id = getGrnId(selectedGrn);
    if (options.some((grn) => getGrnId(grn) === id)) return options;
    return [selectedGrn, ...options];
  }, [options, selectedGrn, isSearching]);

  const noOptionsText = (() => {
    if (loading) return 'Searching…';
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return 'No posted GRNs found';
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedGrn}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedGrn
            && newInput.trim() !== getOptionLabel(selectedGrn)
          ) {
            setSelectedGrn(null);
            onChange('');
          }
        }
      }}
      onChange={(_, grn) => {
        setSelectedGrn(grn);
        onChange(grn ? getGrnId(grn) : '');
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => getGrnId(a) === getGrnId(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
